"use strict";

// One standard worldwide: free shipping at the CAD threshold (theme setting),
// shown in the shopper's currency via Shopify's own presentment rate so
// customers never have to convert anything themselves. Config renders from
// theme settings (Customize → Free shipping); the constant is a fallback.
const FS_CONFIG = window.MOLLY_CONFIG?.freeShipping || {
	shopCurrency: "CAD",
	thresholdCents: 10000,
	thresholdLabel: "C$100",
};

const formatCurrencyAmount = (cents, currencyCode, decimals) => {
	try {
		return new Intl.NumberFormat(undefined, {
			style: "currency",
			currency: currencyCode,
			currencyDisplay: "code",
			minimumFractionDigits: decimals,
			maximumFractionDigits: decimals,
		}).format(cents / 100);
	} catch {
		return `${currencyCode} ${(cents / 100).toFixed(decimals)}`;
	}
};

// Threshold expressed in the shopper's presentment currency. Falls back to
// the CAD amount when Shopify's rate is unavailable (the shipping profile
// evaluates the minimum in CAD anyway).
const getFreeShippingThreshold = () => {
	const activeCurrency = window.Shopify?.currency?.active || FS_CONFIG.shopCurrency;
	if (activeCurrency === FS_CONFIG.shopCurrency) {
		return {
			currencyCode: activeCurrency,
			thresholdCents: FS_CONFIG.thresholdCents,
			thresholdLabel: FS_CONFIG.thresholdLabel,
		};
	}
	const rate = parseFloat(window.Shopify?.currency?.rate);
	if (!Number.isFinite(rate) || rate <= 0) {
		return {
			currencyCode: FS_CONFIG.shopCurrency,
			thresholdCents: FS_CONFIG.thresholdCents,
			thresholdLabel: FS_CONFIG.thresholdLabel,
		};
	}
	const thresholdCents = Math.round(FS_CONFIG.thresholdCents * rate);
	return {
		currencyCode: activeCurrency,
		thresholdCents,
		thresholdLabel: formatCurrencyAmount(thresholdCents, activeCurrency, 0),
	};
};
const ONE_OF_ONE_QUANTITY_MESSAGE = "One of a kind — only one is available.";

const mobileMenuToggle = document.querySelector("[data-mobile-menu-toggle]");
const mobileMenu = document.querySelector("[data-mobile-menu]");

const setMobileMenuOpen = (open) => {
	if (!mobileMenuToggle || !mobileMenu) return;
	mobileMenu.hidden = !open;
	mobileMenuToggle.setAttribute("aria-expanded", String(open));
	mobileMenuToggle.setAttribute("aria-label", open ? "Close menu" : "Open menu");
};

if (mobileMenuToggle && mobileMenu) {
	mobileMenuToggle.addEventListener("click", () => {
		setMobileMenuOpen(mobileMenu.hidden);
	});

	mobileMenu.addEventListener("click", (event) => {
		if (event.target.closest("a")) setMobileMenuOpen(false);
	});

	document.addEventListener("keydown", (event) => {
		if (event.key === "Escape" && !mobileMenu.hidden) {
			setMobileMenuOpen(false);
			mobileMenuToggle.focus();
		}
	});

	window.addEventListener("resize", () => {
		if (window.innerWidth > 900) setMobileMenuOpen(false);
	});
}

const qualifiesForFreeShipping = (priceCents, thresholdCents) => {
	const directPriceCents = Number(priceCents);
	return (
		Number.isFinite(directPriceCents) &&
		directPriceCents > 0 &&
		directPriceCents + 0.5 >= thresholdCents
	);
};

const updateFreeShippingBadge = (badge, priceCents) => {
	if (!badge) return;
	badge.dataset.directPriceCents = String(priceCents || "");

	const threshold = getFreeShippingThreshold();
	badge.textContent = qualifiesForFreeShipping(priceCents, threshold.thresholdCents)
		? "Free shipping"
		: `Free shipping on ${threshold.thresholdLabel}+ orders`;
};

// Cart page progress: recomputes the remaining amount and bar width in the
// shopper's currency (server renders exact values for CAD visitors only).
const initFreeShippingProgress = (root) => {
	root.querySelectorAll("[data-fs-progress]").forEach((progress) => {
		const totalCents = Number(progress.dataset.fsTotal);
		if (!Number.isFinite(totalCents) || totalCents < 0) return;

		const threshold = getFreeShippingThreshold();
		const message = progress.querySelector("[data-fs-message]");
		const fill = progress.querySelector("[data-fs-fill]");
		const remaining = threshold.thresholdCents - totalCents;

		if (message) {
			if (remaining > 0) {
				message.innerHTML = `Add <strong>${formatCurrencyAmount(remaining, threshold.currencyCode, 2)}</strong> more for free shipping.`;
				message.classList.remove("free-shipping-unlocked");
			} else {
				message.textContent = "You've unlocked free shipping.";
				message.classList.add("free-shipping-unlocked");
			}
		}
		if (fill && threshold.thresholdCents > 0) {
			fill.style.width = `${Math.min(100, Math.round((totalCents / threshold.thresholdCents) * 100))}%`;
		}
	});
};

const initFreeShippingBadges = (root) => {
	root.querySelectorAll("[data-free-shipping-badge]").forEach((badge) => {
		updateFreeShippingBadge(badge, badge.dataset.directPriceCents);
	});
};

const validateOneOfOneQuantity = (input) => {
	const isOneOfOne = input.dataset.oneOfOneQuantity === "true";
	const quantity = Number(input.value);
	input.setCustomValidity(
		isOneOfOne && Number.isFinite(quantity) && quantity > 1
			? ONE_OF_ONE_QUANTITY_MESSAGE
			: "",
	);
};

const initOneOfOneQuantityValidation = (root) => {
	root.querySelectorAll("[data-one-of-one-quantity]").forEach((input) => {
		if (input.dataset.oneOfOneValidationBound === "true") return;
		input.dataset.oneOfOneValidationBound = "true";
		input.addEventListener("input", () => validateOneOfOneQuantity(input));
		input.addEventListener("change", () => validateOneOfOneQuantity(input));
		input.addEventListener("invalid", () => validateOneOfOneQuantity(input));
		validateOneOfOneQuantity(input);
	});
};

const initReviewJumps = (root) => {
	root.querySelectorAll('a[href="#ProductReviews"]').forEach((link) => {
		if (link.dataset.reviewJumpBound === "true") return;
		link.dataset.reviewJumpBound = "true";
		link.addEventListener("click", (event) => {
			const target = document.getElementById("ProductReviews");
			if (!target) return;
			event.preventDefault();
			if (window.location.hash !== "#ProductReviews") {
				window.history.pushState({}, "", "#ProductReviews");
			}
			const navigationHeight =
				document.querySelector(".site-nav")?.getBoundingClientRect().height || 0;
			const targetTop = target.getBoundingClientRect().top + window.scrollY;
			const scrollingElement = document.scrollingElement;
			const previousScrollBehavior = scrollingElement.style.scrollBehavior;
			scrollingElement.style.scrollBehavior = "auto";
			scrollingElement.scrollTop = Math.max(
				0,
				targetTop - navigationHeight - 24,
			);
			scrollingElement.style.scrollBehavior = previousScrollBehavior;
			target.focus({ preventScroll: true });
		});
	});
};

const initHeroLookbooks = (root) => {
	root.querySelectorAll("[data-hero-lookbook]").forEach((lookbook) => {
		if (lookbook.dataset.lookbookBound === "true") return;
		lookbook.dataset.lookbookBound = "true";

		const track = lookbook.querySelector("[data-lookbook-track]");
		const slides = Array.from(lookbook.querySelectorAll("[data-lookbook-slide]"));
		const dots = Array.from(lookbook.querySelectorAll("[data-lookbook-dot]"));
		const previous = lookbook.querySelector("[data-lookbook-previous]");
		const next = lookbook.querySelector("[data-lookbook-next]");
		const count = lookbook.querySelector("[data-lookbook-count]");
		const status = lookbook.querySelector("[data-lookbook-status]");
		if (!track || slides.length < 2) return;

		const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
		const autoplayDelay = 6000;
		const manualPauseDelay = 12000;
		let activeIndex = 0;
		let autoplayTimer = null;
		let manualPauseUntil = 0;
		let pointerInside = false;
		let focusInside = false;

		const stopAutoplay = () => {
			window.clearTimeout(autoplayTimer);
			autoplayTimer = null;
		};

		const canAutoplay = () => (
			lookbook.isConnected &&
			!reduceMotion.matches &&
			!document.hidden &&
			!pointerInside &&
			!focusInside
		);

		const updateActive = (index) => {
			activeIndex = Math.max(0, Math.min(index, slides.length - 1));
			slides.forEach((slide, slideIndex) => {
				slide.tabIndex = slideIndex === activeIndex ? 0 : -1;
			});
			dots.forEach((dot, dotIndex) => {
				if (dotIndex === activeIndex) {
					dot.setAttribute("aria-current", "true");
				} else {
					dot.removeAttribute("aria-current");
				}
			});
			if (count) count.textContent = `${activeIndex + 1} / ${slides.length}`;
			if (status) {
				const label = slides[activeIndex].getAttribute("aria-label") || `Slide ${activeIndex + 1}`;
				status.textContent = label;
			}
		};

		const scrollToSlide = (index) => {
			const nextIndex = (index + slides.length) % slides.length;
			track.scrollTo({
				left: slides[nextIndex].offsetLeft,
				behavior: reduceMotion.matches ? "auto" : "smooth",
			});
			updateActive(nextIndex);
		};

		const scheduleAutoplay = (minimumDelay = autoplayDelay) => {
			stopAutoplay();
			if (!canAutoplay()) return;
			const manualDelayRemaining = Math.max(0, manualPauseUntil - Date.now());
			autoplayTimer = window.setTimeout(() => {
				if (!canAutoplay()) return;
				if (Date.now() < manualPauseUntil) {
					scheduleAutoplay();
					return;
				}
				scrollToSlide(activeIndex + 1);
				scheduleAutoplay();
			}, Math.max(minimumDelay, manualDelayRemaining));
		};

		const pauseAfterManualInteraction = () => {
			manualPauseUntil = Date.now() + manualPauseDelay;
			scheduleAutoplay(manualPauseDelay);
		};

		previous?.addEventListener("click", () => {
			scrollToSlide(activeIndex - 1);
			pauseAfterManualInteraction();
		});
		next?.addEventListener("click", () => {
			scrollToSlide(activeIndex + 1);
			pauseAfterManualInteraction();
		});
		dots.forEach((dot, index) => {
			dot.addEventListener("click", () => {
				scrollToSlide(index);
				pauseAfterManualInteraction();
			});
		});

		track.addEventListener("keydown", (event) => {
			if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
			event.preventDefault();
			scrollToSlide(activeIndex + (event.key === "ArrowRight" ? 1 : -1));
			pauseAfterManualInteraction();
		});

		track.addEventListener("pointerdown", pauseAfterManualInteraction, { passive: true });
		track.addEventListener("wheel", pauseAfterManualInteraction, { passive: true });
		lookbook.addEventListener("pointerenter", () => {
			pointerInside = true;
			stopAutoplay();
		});
		lookbook.addEventListener("pointerleave", () => {
			pointerInside = false;
			scheduleAutoplay();
		});
		lookbook.addEventListener("focusin", () => {
			focusInside = true;
			stopAutoplay();
		});
		lookbook.addEventListener("focusout", () => {
			window.setTimeout(() => {
				focusInside = lookbook.contains(document.activeElement);
				if (!focusInside) scheduleAutoplay();
			});
		});
		document.addEventListener("visibilitychange", () => {
			if (document.hidden) {
				stopAutoplay();
			} else {
				scheduleAutoplay();
			}
		});
		reduceMotion.addEventListener("change", () => {
			if (reduceMotion.matches) {
				stopAutoplay();
			} else {
				scheduleAutoplay();
			}
		});

		track.addEventListener("scroll", () => {
			const nearest = slides.reduce((best, slide, index) => {
				const distance = Math.abs(slide.offsetLeft - track.scrollLeft);
				return distance < best.distance ? { index, distance } : best;
			}, { index: 0, distance: Number.POSITIVE_INFINITY });
			updateActive(nearest.index);
		}, { passive: true });

		if (window.ResizeObserver) {
			new ResizeObserver(() => {
				track.scrollTo({ left: slides[activeIndex].offsetLeft, behavior: "auto" });
			}).observe(track);
		}

		updateActive(0);
		scheduleAutoplay();
	});
};

const initProductRecommendations = (root) => {
	root.querySelectorAll("[data-product-discovery]").forEach(async (section) => {
		if (section.dataset.recommendationsLoading === "true") return;
		section.dataset.recommendationsLoading = "true";

		const results = section.querySelector("[data-product-recommendation-results]");
		const endpoint = section.dataset.recommendationsUrl;
		if (!results || !endpoint) return;

		const url = new URL(endpoint, window.location.origin);
		url.searchParams.set("section_id", section.dataset.sectionId);
		url.searchParams.set("product_id", section.dataset.productId);
		url.searchParams.set("limit", "4");
		url.searchParams.set("intent", "related");

		try {
			const response = await fetch(url, { headers: { Accept: "text/html" } });
			if (!response.ok) throw new Error("Product recommendations unavailable");
			const nextDocument = new DOMParser().parseFromString(
				await response.text(),
				"text/html",
			);
			const nextResults = nextDocument.querySelector(
				"[data-product-recommendation-results]",
			);
			if (nextResults?.childElementCount) {
				results.innerHTML = nextResults.innerHTML;
				initFreeShippingBadges(results);
			}
		} catch {
			// The full-collection link remains available when recommendations fail.
		}
	});
};

const syncShopifyLocalization = (nextDocument) => {
	const localizationSource = Array.from(nextDocument.scripts)
		.map((script) => script.textContent)
		.join("\n");
	const currencyMatch = localizationSource.match(
		/Shopify\.currency\s*=\s*(\{[^;]+\})\s*;/,
	);
	const countryMatch = localizationSource.match(
		/Shopify\.country\s*=\s*("[^"]+")\s*;/,
	);
	if (!currencyMatch || !countryMatch) return;

	try {
		window.Shopify = window.Shopify || {};
		window.Shopify.currency = JSON.parse(currencyMatch[1]);
		window.Shopify.country = JSON.parse(countryMatch[1]);
	} catch {
		// Keep the conservative threshold message when localization data is unavailable.
	}
};

const initProductSections = (root) => {
	root.querySelectorAll("[data-product-section]").forEach((section) => {
		const variantSelect = section.querySelector("[data-variant-select]");
		const request = section.querySelector("[data-sold-out-request]");
		const requestToggle = section.querySelector("[data-request-toggle]");
		const requestFields = section.querySelector("[data-request-fields]");

		requestToggle?.addEventListener("click", () => {
			const open = requestToggle.getAttribute("aria-expanded") === "true";
			requestToggle.setAttribute("aria-expanded", String(!open));
			requestFields.hidden = open;
			if (!open) {
				requestFields.querySelector("input:not([type='hidden']), textarea")?.focus();
			}
		});

		if (!variantSelect) return;

		const pricing = section.querySelector("[data-product-pricing]");
		const directPriceNote = section.querySelector("[data-direct-price-note]");
		const savings = section.querySelector("[data-product-savings]");
		const price = section.querySelector("[data-product-price]");
		const comparePrice = section.querySelector("[data-compare-price]");
		const shippingBadge = section.querySelector("[data-free-shipping-badge]");
		const availability = section.querySelector("[data-availability]");
		const addButton = section.querySelector("[data-add-to-cart]");
		const checkout = section.querySelector("[data-accelerated-checkout]");
		const quantity = section.querySelector("[data-quantity-input]");
		const purchaseControls = section.querySelector("[data-purchase-controls]");
		const requestVariant = section.querySelector("[data-request-variant]");
		const requestProductUrl = section.querySelector("[data-request-product-url]");

		variantSelect.addEventListener("change", () => {
			const option = variantSelect.selectedOptions[0];
			const available = option.dataset.available === "true";
			const oneOfOne = option.dataset.oneOfOne === "true";
			const hasMarketplacePrice = Boolean(option.dataset.comparePrice);

			price.textContent = option.dataset.price;
			updateFreeShippingBadge(shippingBadge, option.dataset.priceCents);
			comparePrice.textContent = option.dataset.comparePrice || "";
			pricing?.classList.toggle("is-marketplace-priced", hasMarketplacePrice);
			comparePrice.hidden = !hasMarketplacePrice;
			directPriceNote.hidden = !hasMarketplacePrice;
			savings.hidden = !hasMarketplacePrice;
			savings.textContent = hasMarketplacePrice
				? `(${option.dataset.savingsPercent}% off)`
				: "";
			availability.textContent = available
				? oneOfOne
					? "One of a kind · Ready to ship"
					: "Ready to ship"
				: "Sold out";
			availability.classList.toggle("unavailable", !available);
			addButton.disabled = !available;
			addButton.textContent = available ? "Add to cart" : "Sold out";
			checkout.classList.toggle("is-hidden", !available);
			purchaseControls.hidden = !available;
			request.hidden = available;
			if (available && requestToggle && requestFields) {
				requestToggle.setAttribute("aria-expanded", "false");
				requestFields.hidden = true;
			}

			const inventory = Number(option.dataset.inventoryQuantity);
			if (option.dataset.inventoryPolicy === "deny" && inventory > 0) {
				quantity.max = String(inventory);
				quantity.value = String(Math.min(Number(quantity.value), inventory));
			} else {
				quantity.removeAttribute("max");
			}
			quantity.dataset.oneOfOneQuantity = String(oneOfOne);
			validateOneOfOneQuantity(quantity);

			const media = option.dataset.mediaId
				? section.querySelector(`[data-media-id="${option.dataset.mediaId}"]`)
				: null;
			media?.scrollIntoView({ behavior: "smooth", block: "center" });

			const url = new URL(window.location.href);
			url.searchParams.set("variant", option.value);
			window.history.replaceState({}, "", url);
			if (requestVariant) requestVariant.value = option.dataset.variantTitle || "";
			if (requestProductUrl) requestProductUrl.value = url.toString();
		});
	});
};

const initDynamic = (root) => {
	initFreeShippingBadges(root);
	initFreeShippingProgress(root);
	initOneOfOneQuantityValidation(root);
	initReviewJumps(root);
	initHeroLookbooks(root);
	initProductSections(root);
	initProductRecommendations(root);
};

// Swap only the localized markup so a currency change keeps the shopper on the same
// URL, query string and scroll position instead of reloading onto the home page.
const applyLocalization = async (form) => {
	await fetch(form.action, {
		method: "POST",
		body: new FormData(form),
		headers: { Accept: "text/html" },
	});

	const response = await fetch(window.location.href, {
		headers: { Accept: "text/html" },
		cache: "reload",
	});
	const nextDocument = new DOMParser().parseFromString(await response.text(), "text/html");
	syncShopifyLocalization(nextDocument);

	const currentMain = document.getElementById("MainContent");
	const nextMain = nextDocument.getElementById("MainContent");
	if (!currentMain || !nextMain) throw new Error("Localized content missing");

	currentMain.replaceWith(nextMain);
	document.querySelectorAll("[data-country-selector]").forEach((selector) => {
		const source = nextDocument.getElementById(selector.id);
		if (source) selector.value = source.value;
	});
	initDynamic(document);
};

document.querySelectorAll("[data-country-selector]").forEach((selector) => {
	selector.addEventListener("change", async () => {
		const form = selector.form;
		if (!form) return;

		selector.setAttribute("aria-busy", "true");
		try {
			await applyLocalization(form);
			setMobileMenuOpen(false);
		} catch (error) {
			form.submit();
		} finally {
			selector.removeAttribute("aria-busy");
		}
	});
});

initDynamic(document);